import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, type AnalyticsEventName, type AnalyticsProperties } from '@/lib/track';

/**
 * Returns a `fire(key, name, props)` function that only ever sends a given key once
 * per page view — immune to React re-renders and Strict Mode double effects.
 */
export function useTrackOnce() {
  const location = useLocation();
  const sent = useRef<Set<string>>(new Set());

  useEffect(() => {
    sent.current = new Set();
  }, [location.pathname, location.search]);

  return useCallback((key: string, name: AnalyticsEventName, props?: AnalyticsProperties) => {
    if (sent.current.has(key)) return;
    sent.current.add(key);
    trackEvent(name, props);
  }, []);
}

/** Fires scroll_depth at 50% and 90% — once per threshold per page view. */
export function useScrollDepth(thresholds: number[] = [50, 90]) {
  const location = useLocation();

  useEffect(() => {
    const hit = new Set<number>();
    let frame = 0;

    const measure = () => {
      frame = 0;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      for (const t of thresholds) {
        if (percent >= t && !hit.has(t)) {
          hit.add(t);
          trackEvent('scroll_depth', { percent: t });
        }
      }
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
}

/**
 * Tracks engaged time on the current page (tab visible only).
 * Returns a getter so consumers can attach engaged_seconds to events.
 */
export function useEngagedTime() {
  const location = useLocation();
  const engagedMs = useRef(0);
  const lastTick = useRef<number | null>(null);

  useEffect(() => {
    engagedMs.current = 0;
    lastTick.current = document.visibilityState === 'visible' ? Date.now() : null;

    const flush = () => {
      if (lastTick.current !== null) {
        engagedMs.current += Date.now() - lastTick.current;
        lastTick.current = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        lastTick.current = Date.now();
      } else {
        flush();
      }
    };

    const interval = window.setInterval(() => {
      if (lastTick.current !== null) {
        engagedMs.current += Date.now() - lastTick.current;
        lastTick.current = Date.now();
      }
    }, 1000);

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      flush();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [location.pathname, location.search]);

  return useCallback(() => Math.round(engagedMs.current / 1000), []);
}

/** Tracks the deepest scroll percentage reached on the current page view. */
export function useMaxScrollDepth() {
  const location = useLocation();
  const max = useRef(0);

  useEffect(() => {
    max.current = 0;
    const onScroll = () => {
      const doc = document.documentElement;
      if (doc.scrollHeight <= window.innerHeight) return;
      const percent = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
      max.current = Math.max(max.current, Math.min(100, Math.round(percent)));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname, location.search]);

  return useCallback(() => max.current, []);
}

interface MeaningfulViewOptions {
  /** Fraction of the element that must be visible. */
  threshold?: number;
  /** How long it must stay visible before counting as a view (ms). */
  dwellMs?: number;
  /** Fire only the first time. */
  once?: boolean;
  enabled?: boolean;
}

/**
 * Calls `onView` once the element has been at least `threshold` visible for
 * `dwellMs` continuously — used for project impressions and substantial section views.
 * Scroll-past does not count.
 */
export function useMeaningfulView<T extends Element>(
  onView: () => void,
  { threshold = 0.5, dwellMs = 1000, once = true, enabled = true }: MeaningfulViewOptions = {}
) {
  const ref = useRef<T | null>(null);
  const fired = useRef(false);
  const callback = useRef(onView);
  callback.current = onView;

  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    let timer: number | undefined;
    const clear = () => {
      if (timer) window.clearTimeout(timer);
      timer = undefined;
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          if (once && fired.current) return;
          clear();
          timer = window.setTimeout(() => {
            if (once && fired.current) return;
            fired.current = true;
            callback.current();
          }, dwellMs);
        } else {
          clear();
        }
      },
      { threshold: [0, threshold, Math.min(1, threshold + 0.25)] }
    );

    observer.observe(el);
    return () => {
      clear();
      observer.disconnect();
    };
  }, [threshold, dwellMs, once, enabled]);

  return ref;
}
